// Import controller interfaces
import {
    INewBusinessApplicationController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api models.
import {
    AcknowledgementText,
    Address,
    AddressLocationAsMailingAddress,
    AddressLocationWithAccuracy,
    BindingAuthorityEligibilityResult,
    BroadLineOfBusinessDetails,
    BuildingLimitsCommercial,
    BuildingLimitsPersonal,
    BuildingRequestCommercialExisting,
    BuildingRequestPersonal,
    BuildingResponseCommercialExistingWithStatus,
    BuildingResponsePersonalWithStatus,
    BuildingRiskDataCommercial,
    BuildingRiskDataPersonal,
    DisqualifyingConditionsAgreement,
    DocumentUpload,
    EligibilityStatus,
    GpsCoordinatesWithAccuracy,
    InsuredInfo,
    InsuredInfoOfac,
    InsuredName,
    MortgageeRequest,
    MortgageeRequestWithStatusAndId,
    MortgageeStatusInfo,
    MortgageeWithStatus,
    NewBusinessApplication,
    NewBusinessApplicationDocument,
    NewBusinessApplicationStatusInfo,
    NewBusinessApplicationStatusSummary,
    PerilDeductible,
    PhoneNumber,
    PolicyCost,
    PolicyCostLineItem,
    PossibleDisclosureTexts,
    PossiblePeril,
    Section,
    SectionStatusInfo,
    Signature,
} from '@app/models/api';
// Import enumerations.
import {
    BroadLineOfBusiness,
    EligibilityStatusCode,
    GpsAccuracy,
    NewBusinessApplicationSectionName,
    NewBusinessApplicationStatus,
    Peril,
    PhoneContactType,
    RecordStatus,
    SectionStatus,
    SigningParty,
} from '@app/enumerations';

export default class MockNewBusinessApplicationController implements INewBusinessApplicationController {

    public constructor() {
        
    }

    public async startNewBusinessApplicationPersonal(propertyAddress: AddressLocationAsMailingAddress,
        perilsDeductibles: PerilDeductible[],
        disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        return await this.getNewBusinessApplication(123456);
    }

    public async startNewBusinessApplicationCommercial(propertyAddress: AddressLocationAsMailingAddress,
        perilsDeductibles: PerilDeductible[],
        disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        return await this.getNewBusinessApplication(123456);
    }

    public async rateNewBusinessApplicationPersonal(newBusinessApplicationId: number,
        perilsDeductibles?: PerilDeductible[],
        risk?: BuildingRequestPersonal,
        disqualifyingConditionsAgreement?: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        return await this.getNewBusinessApplication(newBusinessApplicationId);
    }

    public async rateNewBusinessApplicationCommercial(newBusinessApplicationId: number,
        includeTriaCoverage?: boolean,
        perilsDeductibles?: PerilDeductible[],
        risks?: BuildingRequestCommercialExisting[],
        disqualifyingConditionsAgreement?: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        return await this.getNewBusinessApplication(newBusinessApplicationId);
    }

    public async getNewBusinessApplication(newBusinessApplicationId: number): Promise<NewBusinessApplication> {
        let newBusinessApplication: NewBusinessApplication = new NewBusinessApplication();
        // NewBusinessApplication id.
        newBusinessApplication.id = newBusinessApplicationId;

        // Broad line of business.
        let broadLineOfBusiness: BroadLineOfBusinessDetails = new BroadLineOfBusinessDetails();
        broadLineOfBusiness.code = newBusinessApplicationId !== 123456 ? BroadLineOfBusiness.COMMERCIAL : BroadLineOfBusiness.PERSONAL;
        broadLineOfBusiness.friendlyName = newBusinessApplicationId !== 123456 ? 'Commercial' : 'Personal';
        newBusinessApplication.broadLineOfBusiness = broadLineOfBusiness;

        // Acknowledgement texts.
        let acknowledgementText1: AcknowledgementText = new AcknowledgementText();
        acknowledgementText1.isAcknowledged = false;
        acknowledgementText1.textId = 1;

        let acknowledgementText2: AcknowledgementText = new AcknowledgementText();
        acknowledgementText2.isAcknowledged = false;
        acknowledgementText2.textId = 2;

        let acknowledgementText3: AcknowledgementText = new AcknowledgementText();
        acknowledgementText3.isAcknowledged = false;
        acknowledgementText3.textId = 3;

        newBusinessApplication.acknowledgements = [
            acknowledgementText1,
            acknowledgementText2,
            acknowledgementText3,
        ];

        // Premium.
        let policyCostLineItem1: PolicyCostLineItem = new PolicyCostLineItem();
        policyCostLineItem1.amount = 663.03;
        policyCostLineItem1.lineItemNumber = 1;
        policyCostLineItem1.name = 'Premium';

        let policyCostLineItem2: PolicyCostLineItem = new PolicyCostLineItem();
        policyCostLineItem2.amount = 75;
        policyCostLineItem2.lineItemNumber = 2;
        policyCostLineItem2.name = 'Policy Fee';

        let policyCostLineItem3: PolicyCostLineItem = new PolicyCostLineItem();
        policyCostLineItem3.amount = 7.38;
        policyCostLineItem3.lineItemNumber = 3;
        policyCostLineItem3.name = 'Stamping Fee';

        let policyCostLineItem4: PolicyCostLineItem = new PolicyCostLineItem();
        policyCostLineItem4.amount = 19.93;
        policyCostLineItem4.lineItemNumber = 4;
        policyCostLineItem4.name = 'State Tax';

        let policyCost: PolicyCost = new PolicyCost();
        policyCost.totalCost = 765.34;
        policyCost.lineItems = [
            policyCostLineItem1,
            policyCostLineItem2,
            policyCostLineItem3,
            policyCostLineItem4,
        ];

        newBusinessApplication.cost = policyCost;

        // Disqualifying condition agreement.
        let disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement = new DisqualifyingConditionsAgreement();
        disqualifyingConditionsAgreement.riskMeetsEligibilityRequirements = true;
        disqualifyingConditionsAgreement.version = 1;

        newBusinessApplication.disqualifyingConditionsAgreement = disqualifyingConditionsAgreement;

        // Include tria coverage.
        if (newBusinessApplication.broadLineOfBusiness.code === BroadLineOfBusiness.COMMERCIAL) {
            newBusinessApplication.includeTriaCoverage = true;
        }
        else {
            newBusinessApplication.includeTriaCoverage = null;
        }

        // Mortgagees
        let mortgageeAddress1: Address = new Address();
        mortgageeAddress1.addressLine1 = '101 state st';
        mortgageeAddress1.addressLine2 = 'Test City';
        mortgageeAddress1.state = 'MT';
        mortgageeAddress1.postalCode = '11111-1111';

        let mortgageeStatusInfo1: MortgageeStatusInfo = new MortgageeStatusInfo();
        mortgageeStatusInfo1.statusCode = RecordStatus.COMPLETE;
        mortgageeStatusInfo1.statusMessage = 'Complete';

        let mortgagee1: MortgageeWithStatus = new MortgageeWithStatus();
        mortgagee1.billMortgagee = true;
        mortgagee1.coverageRequiredByMortgagee = true;
        mortgagee1.email = 'test@test.com';
        mortgagee1.id = '1';
        mortgagee1.includeAsTheirInterestsMayAppear = true;
        mortgagee1.includeItsSuccessorsAndOrAssigns = true;
        mortgagee1.lienPositionNumber = 1;
        mortgagee1.loanNumber = '1';
        mortgagee1.mailingAddress = mortgageeAddress1;
        mortgagee1.name = 'Mortgage Bank 1';
        mortgagee1.sendPaymentReceiptToEmail = false;
        mortgagee1.status = mortgageeStatusInfo1;

        let mortgageeAddress2: Address = new Address();
        mortgageeAddress2.addressLine1 = '202 state st';
        mortgageeAddress2.city = 'Test City';
        mortgageeAddress2.state = 'MT';
        mortgageeAddress2.postalCode = '22222-2222';

        let mortgageeStatusInfo2: MortgageeStatusInfo = new MortgageeStatusInfo();
        mortgageeStatusInfo2.statusCode = RecordStatus.COMPLETE;
        mortgageeStatusInfo2.statusMessage = 'Complete';

        let mortgagee2: MortgageeWithStatus = new MortgageeWithStatus();
        mortgagee2.billMortgagee = true;
        mortgagee2.coverageRequiredByMortgagee = true;
        mortgagee2.email = 'test@test.com';
        mortgagee2.id = '2';
        mortgagee2.includeAsTheirInterestsMayAppear = true;
        mortgagee2.includeItsSuccessorsAndOrAssigns = true;
        mortgagee2.lienPositionNumber = 2;
        mortgagee2.loanNumber = '2';
        mortgagee2.mailingAddress = mortgageeAddress2;
        mortgagee2.name = 'Mortgage Bank 2';
        mortgagee2.sendPaymentReceiptToEmail = false;
        mortgagee2.status = mortgageeStatusInfo2;

        let mortgageeAddress3: Address = new Address();
        mortgageeAddress3.addressLine1 = '303 state st';
        mortgageeAddress3.city = 'Test City';
        mortgageeAddress3.state = 'MT';
        mortgageeAddress3.postalCode = '33333-3333';

        let mortgageeStatusInfo3: MortgageeStatusInfo = new MortgageeStatusInfo();
        mortgageeStatusInfo3.statusCode = RecordStatus.COMPLETE;
        mortgageeStatusInfo3.statusMessage = 'Complete';

        let mortgagee3: MortgageeWithStatus = new MortgageeWithStatus();
        mortgagee3.billMortgagee = true;
        mortgagee3.coverageRequiredByMortgagee = true;
        mortgagee3.email = 'test@test.com';
        mortgagee3.id = '3';
        mortgagee3.includeAsTheirInterestsMayAppear = true;
        mortgagee3.lienPositionNumber = 3;
        mortgagee3.loanNumber = '3';
        mortgagee3.mailingAddress = mortgageeAddress3;
        mortgagee3.name = 'Mortgage Bank 3';
        mortgagee3.sendPaymentReceiptToEmail = false;
        mortgagee3.status = mortgageeStatusInfo3;

        newBusinessApplication.mortgagees = [
            mortgagee1,
            mortgagee2,
            mortgagee3,
        ];

        // Named insured.
        let insuredAddress: Address = new Address();
        insuredAddress.addressLine1 = '123 state st';
        insuredAddress.city = 'Test City';
        insuredAddress.state = 'MT';
        insuredAddress.postalCode = '12345-6789';

        let insuredName: InsuredName = new InsuredName();
        insuredName.firstNameOrCompanyName = newBusinessApplication.broadLineOfBusiness.code !== BroadLineOfBusiness.COMMERCIAL ? 'Test' : 'Test Inc.';
        insuredName.lastName = newBusinessApplication.broadLineOfBusiness.code !== BroadLineOfBusiness.COMMERCIAL ? 'Testerson' : null;

        let phone1: PhoneNumber = new PhoneNumber();
        phone1.contactType = PhoneContactType.HOME;
        phone1.number = '8888888888';

        let namedInsured: InsuredInfoOfac = new InsuredInfoOfac();
        namedInsured.email = 'test-insured@test.com';
        namedInsured.isValidOfac = true;
        namedInsured.mailingAddress = insuredAddress;
        namedInsured.name = insuredName;
        namedInsured.phoneNumbers = [
            phone1
        ];
        namedInsured.sendPaymentReceiptToEmail = false;

        newBusinessApplication.namedInsured = namedInsured;

        // Perils and deductibles.
        let perilDeductibleFl: PerilDeductible = new PerilDeductible();
        perilDeductibleFl.deductibleCode = 'SPEC';
        perilDeductibleFl.perilCode = Peril.FLOOD;

        let perilDeductibleEq: PerilDeductible = new PerilDeductible();
        perilDeductibleEq.deductibleCode = 'SPEC';
        perilDeductibleEq.perilCode = Peril.EARTHQUAKE;

        newBusinessApplication.perilsDeductibles = [
            perilDeductibleFl,
            perilDeductibleEq,
        ];

        // Product code.
        newBusinessApplication.productCode = '591';

        // Requested inception date.
        newBusinessApplication.requestedInceptionDate = new Date();

        // Risk data.
        if (newBusinessApplication.broadLineOfBusiness.code === BroadLineOfBusiness.PERSONAL) {
            let propertyAddress: Address = new Address();
            propertyAddress.addressLine1 = '123 state st';
            propertyAddress.city = 'Test City';
            propertyAddress.state = 'MT';
            propertyAddress.postalCode = '12345-6789';

            let gpsCoordinates: GpsCoordinatesWithAccuracy = new GpsCoordinatesWithAccuracy();
            gpsCoordinates.gpsAccuracy = GpsAccuracy.ROOFTOP;
            gpsCoordinates.latitude = 40.688986334090934;
            gpsCoordinates.longitude = -111.87082968574524;

            let propertyAddressWithLocation: AddressLocationWithAccuracy = new AddressLocationWithAccuracy();
            propertyAddressWithLocation.address = propertyAddress;
            propertyAddressWithLocation.gpsCoordinates = gpsCoordinates;

            let buildingData: BuildingRiskDataPersonal = new BuildingRiskDataPersonal();
            buildingData.buildingReplacementCostValue = 250000;
            buildingData.buildingUse = 'Owner';
            buildingData.constructionType = 'WOOFRA';
            buildingData.floodZone = 'V';
            buildingData.foundationTypeDetail = 'BelowGrade';
            buildingData.numberStoriesAboveGrade = 1;
            buildingData.occupancyType = '1002';
            buildingData.squareFeet = 2000;
            buildingData.yearOfConstruction = 2000

            let buildingLimits: BuildingLimitsPersonal = new BuildingLimitsPersonal();
            buildingLimits.additionalLivingExpense = 25000;
            buildingLimits.building = 2500000;
            buildingLimits.contents = 50000;
            buildingLimits.contentsUseReplacementCostValue = true;
            buildingLimits.otherStructures = 10000;

            let buildingPersonal: BuildingResponsePersonalWithStatus = new BuildingResponsePersonalWithStatus();
            buildingPersonal.address = propertyAddressWithLocation;
            buildingPersonal.buildingData = buildingData;
            buildingPersonal.limits = buildingLimits;

            newBusinessApplication.risk = buildingPersonal;
        }
        else {
            //// Building 1
            let buildingAddress1: Address = new Address();
            buildingAddress1.addressLine1 = '101 state st';
            buildingAddress1.city = 'Test City';
            buildingAddress1.state = 'MT';
            buildingAddress1.postalCode = '11111-1111';

            let gpsCoordinates1: GpsCoordinatesWithAccuracy = new GpsCoordinatesWithAccuracy();
            gpsCoordinates1.gpsAccuracy = GpsAccuracy.ROOFTOP;
            gpsCoordinates1.latitude = 40.688986334090934;
            gpsCoordinates1.longitude = -111.87082968574524;

            let propertyAddressWithLocation1: AddressLocationWithAccuracy = new AddressLocationWithAccuracy();
            propertyAddressWithLocation1.address = buildingAddress1;
            propertyAddressWithLocation1.gpsCoordinates = gpsCoordinates1;

            let buildingData1: BuildingRiskDataCommercial = new BuildingRiskDataCommercial();
            buildingData1.buildingDescription = 'Building #1';
            buildingData1.buildingReplacementCostValue = 250000;
            buildingData1.buildingUse = 'Apartment';
            buildingData1.constructionType = 'WOOFRA';
            buildingData1.floodZone = 'V';
            buildingData1.foundationTypeDetail = 'BelowGrade';
            buildingData1.numberStoriesAboveGrade = 1;
            buildingData1.parkingType = 'Detached';
            buildingData1.squareFeet = 2000;
            buildingData1.yearOfConstruction = 2000

            let buildingLimits1: BuildingLimitsCommercial = new BuildingLimitsCommercial();
            buildingLimits1.building = 2500000;
            buildingLimits1.contents = 50000;
            buildingLimits1.businessIncomeExtraExpense = 10000;

            let buildingCommercial1: BuildingResponseCommercialExistingWithStatus = new BuildingResponseCommercialExistingWithStatus();
            buildingCommercial1.id = '1';
            buildingCommercial1.address = propertyAddressWithLocation1;
            buildingCommercial1.buildingData = buildingData1;
            buildingCommercial1.limits = buildingLimits1;

            //// Building 2
            let buildingAddress2: Address = new Address();
            buildingAddress2.addressLine1 = '202 state st';
            buildingAddress2.city = 'Test City';
            buildingAddress2.state = 'MT';
            buildingAddress2.postalCode = '22222-2222';

            let gpsCoordinates2: GpsCoordinatesWithAccuracy = new GpsCoordinatesWithAccuracy();
            gpsCoordinates2.gpsAccuracy = GpsAccuracy.ROOFTOP;
            gpsCoordinates2.latitude = 40.688986334090934;
            gpsCoordinates2.longitude = -222.87082968574524;

            let propertyAddressWithLocation2: AddressLocationWithAccuracy = new AddressLocationWithAccuracy();
            propertyAddressWithLocation2.address = buildingAddress2;
            propertyAddressWithLocation2.gpsCoordinates = gpsCoordinates2;

            let buildingData2: BuildingRiskDataCommercial = new BuildingRiskDataCommercial();
            buildingData2.buildingDescription = 'Building #2';
            buildingData2.buildingReplacementCostValue = 250000;
            buildingData2.buildingUse = 'Apartment';
            buildingData2.constructionType = 'WOOFRA';
            buildingData2.floodZone = 'V';
            buildingData2.foundationTypeDetail = 'BelowGrade';
            buildingData2.numberStoriesAboveGrade = 2;
            buildingData2.parkingType = 'Detached';
            buildingData2.squareFeet = 2000;
            buildingData2.yearOfConstruction = 2000

            let buildingLimits2: BuildingLimitsCommercial = new BuildingLimitsCommercial();
            buildingLimits2.building = 2500000;
            buildingLimits2.contents = 50000;
            buildingLimits2.businessIncomeExtraExpense = 20000;

            let buildingCommercial2: BuildingResponseCommercialExistingWithStatus = new BuildingResponseCommercialExistingWithStatus();
            buildingCommercial2.id = '2';
            buildingCommercial2.address = propertyAddressWithLocation2;
            buildingCommercial2.buildingData = buildingData2;
            buildingCommercial2.limits = buildingLimits2;

            //// Building 3
            let buildingAddress3: Address = new Address();
            buildingAddress3.addressLine1 = '303 state st';
            buildingAddress3.city = 'Test City';
            buildingAddress3.state = 'MT';
            buildingAddress3.postalCode = '33333-3333';

            let gpsCoordinates3: GpsCoordinatesWithAccuracy = new GpsCoordinatesWithAccuracy();
            gpsCoordinates3.gpsAccuracy = GpsAccuracy.ROOFTOP;
            gpsCoordinates3.latitude = 40.688986334090934;
            gpsCoordinates3.longitude = -333.87082968574524;

            let propertyAddressWithLocation3: AddressLocationWithAccuracy = new AddressLocationWithAccuracy();
            propertyAddressWithLocation3.address = buildingAddress3;
            propertyAddressWithLocation3.gpsCoordinates = gpsCoordinates3;

            let buildingData3: BuildingRiskDataCommercial = new BuildingRiskDataCommercial();
            buildingData3.buildingDescription = 'Building #3';
            buildingData3.buildingReplacementCostValue = 250000;
            buildingData3.buildingUse = 'Apartment';
            buildingData3.constructionType = 'WOOFRA';
            buildingData3.floodZone = 'V';
            buildingData3.foundationTypeDetail = 'BelowGrade';
            buildingData3.numberStoriesAboveGrade = 3;
            buildingData3.parkingType = 'Detached';
            buildingData3.squareFeet = 2000;
            buildingData3.yearOfConstruction = 2000

            let buildingLimits3: BuildingLimitsCommercial = new BuildingLimitsCommercial();
            buildingLimits3.building = 2500000;
            buildingLimits3.contents = 50000;
            buildingLimits3.businessIncomeExtraExpense = 30000;

            let buildingCommercial3: BuildingResponseCommercialExistingWithStatus = new BuildingResponseCommercialExistingWithStatus();
            buildingCommercial3.id = '3';
            buildingCommercial3.address = propertyAddressWithLocation3;
            buildingCommercial3.buildingData = buildingData3;
            buildingCommercial3.limits = buildingLimits3;

            newBusinessApplication.risks = [
                buildingCommercial1,
                buildingCommercial2,
                buildingCommercial3,
            ];
        }
        // Signature
        let signature: Signature = new Signature();
        signature.signatureText = 'Test Testerson';
        signature.signingParty = SigningParty.PRODUCER;
        newBusinessApplication.signature = signature;

        // Status
        let status: NewBusinessApplicationStatusInfo = new NewBusinessApplicationStatusInfo();
        status.statusCode = newBusinessApplication.id === 456789 ? NewBusinessApplicationStatus.APPROVED : (newBusinessApplication.id === 234567 ? NewBusinessApplicationStatus.SUBMITTED : NewBusinessApplicationStatus.IN_THE_WORKS)
        status.statusMessage = 'in the works';

        let sections: Section<NewBusinessApplicationSectionName>[] = [];

        if (newBusinessApplication.broadLineOfBusiness.code === BroadLineOfBusiness.PERSONAL) {
            let sectionStatusInfo1 = new SectionStatusInfo();
            sectionStatusInfo1.statusCode = SectionStatus.COMPLETE;
            sectionStatusInfo1.statusMessage = '';

            let section1: Section<NewBusinessApplicationSectionName> = new Section();
            section1.friendlyName = 'Property Address';
            section1.isEditable = true;
            section1.name = NewBusinessApplicationSectionName.PROPERTY_ADDRESS;
            section1.status = sectionStatusInfo1;

            sections.push(section1);
        }

        let sectionStatusInfo2 = new SectionStatusInfo();
        sectionStatusInfo2.statusCode = SectionStatus.COMPLETE;
        sectionStatusInfo2.statusMessage = '';

        let section2: Section<NewBusinessApplicationSectionName> = new Section();
        section2.friendlyName = 'Perils';
        section2.isEditable = true;
        section2.name = NewBusinessApplicationSectionName.PERILS;
        section2.status = sectionStatusInfo2;

        sections.push(section2);

        let sectionStatusInfo3 = new SectionStatusInfo();
        sectionStatusInfo3.statusCode = SectionStatus.COMPLETE;
        sectionStatusInfo3.statusMessage = '';

        let section3: Section<NewBusinessApplicationSectionName> = new Section();
        section3.friendlyName = 'Appetite & Eligibility';
        section3.isEditable = true;
        section3.name = NewBusinessApplicationSectionName.APPETITE_AND_ELIGIBILITY;
        section3.status = sectionStatusInfo3;

        sections.push(section3);

        if (newBusinessApplication.broadLineOfBusiness.code === BroadLineOfBusiness.PERSONAL) {
            let sectionStatusInfo4 = new SectionStatusInfo();
            sectionStatusInfo4.statusCode = SectionStatus.REQUIRED;
            sectionStatusInfo4.statusMessage = 'Dwelling is incomplete';

            let section4: Section<NewBusinessApplicationSectionName> = new Section();
            section4.friendlyName = 'Dwelling';
            section4.isEditable = true;
            section4.name = NewBusinessApplicationSectionName.DWELLING;
            section4.status = sectionStatusInfo4;

            sections.push(section4);
        }
        else {
            let sectionStatusInfo4 = new SectionStatusInfo();
            sectionStatusInfo4.statusCode = SectionStatus.COMPLETE;
            sectionStatusInfo4.statusMessage = 'Buildings are incomplete';

            let section4: Section<NewBusinessApplicationSectionName> = new Section();
            section4.friendlyName = 'Buildings';
            section4.isEditable = true;
            section4.name = NewBusinessApplicationSectionName.BUILDINGS;
            section4.status = sectionStatusInfo4;

            sections.push(section4);
        }

        let sectionStatusInfo5 = new SectionStatusInfo();
        sectionStatusInfo5.statusCode = SectionStatus.COMPLETE;
        sectionStatusInfo5.statusMessage = 'Coverage limits are incomplete';

        let section5: Section<NewBusinessApplicationSectionName> = new Section();
        section5.friendlyName = 'Coverage Limits';
        section5.isEditable = true;
        section5.name = NewBusinessApplicationSectionName.COVERAGE_LIMITS;
        section5.status = sectionStatusInfo5;

        sections.push(section5);

        let sectionStatusInfo6 = new SectionStatusInfo();
        sectionStatusInfo6.statusCode = newBusinessApplication.id === 234567 || newBusinessApplication.id === 456789 ? SectionStatus.COMPLETE : SectionStatus.REQUIRED;
        sectionStatusInfo6.statusMessage = 'Insured information is incomplete';

        let section6: Section<NewBusinessApplicationSectionName> = new Section();
        section6.friendlyName = 'Insured';
        section6.isEditable = true;
        section6.name = NewBusinessApplicationSectionName.INSURED;
        section6.status = sectionStatusInfo6;

        sections.push(section6);

        let sectionStatusInfo7 = new SectionStatusInfo();
        sectionStatusInfo7.statusCode = newBusinessApplication.id === 234567 || newBusinessApplication.id === 456789 ? SectionStatus.COMPLETE : SectionStatus.REQUIRED;
        sectionStatusInfo7.statusMessage = 'Mortgagees are incomplete';

        let section7: Section<NewBusinessApplicationSectionName> = new Section();
        section7.friendlyName = 'Mortgagees';
        section7.isEditable = true;
        section7.name = NewBusinessApplicationSectionName.MORTGAGEES;
        section7.status = sectionStatusInfo7;

        sections.push(section7);

        let sectionStatusInfo8 = new SectionStatusInfo();
        sectionStatusInfo8.statusCode = newBusinessApplication.id === 234567 || newBusinessApplication.id === 456789 ? SectionStatus.COMPLETE : SectionStatus.NOT_ELIGIBLE;
        sectionStatusInfo8.statusMessage = 'Inception date is incomplete';

        let section8: Section<NewBusinessApplicationSectionName> = new Section();
        section8.friendlyName = 'Inception Date';
        section8.isEditable = true;
        section8.name = NewBusinessApplicationSectionName.INCEPTION_DATE;
        section8.status = sectionStatusInfo8;

        sections.push(section8);

        let sectionStatusInfo9 = new SectionStatusInfo();
        sectionStatusInfo9.statusCode = newBusinessApplication.id === 234567 || newBusinessApplication.id === 456789 ? SectionStatus.COMPLETE : SectionStatus.REQUIRED;
        sectionStatusInfo9.statusMessage = 'Disclosures are incomplete';

        let section9: Section<NewBusinessApplicationSectionName> = new Section();
        section9.friendlyName = 'Disclosures';
        section9.isEditable = true;
        section9.name = NewBusinessApplicationSectionName.DISCLOSURES;
        section9.status = sectionStatusInfo9;

        sections.push(section9);

        let sectionStatusInfo10 = new SectionStatusInfo();
        sectionStatusInfo10.statusCode = SectionStatus.REQUIRED;
        sectionStatusInfo10.statusMessage = '';

        let section10: Section<NewBusinessApplicationSectionName> = new Section();
        section10.friendlyName = 'Pay & Issue Policy';
        section10.isEditable = true;
        section10.name = NewBusinessApplicationSectionName.PAY_AND_ISSUE_POLICY;
        section10.status = sectionStatusInfo10;

        sections.push(section10);

        let eligibilityStatus: EligibilityStatus = new EligibilityStatus();
        eligibilityStatus.statusCode = newBusinessApplication.id !== 345678 ? EligibilityStatusCode.PASS : EligibilityStatusCode.FAIL;
        eligibilityStatus.problems = [
            'Condo risks under course of construction are not allowed.',
            'Property specified in a flood zone `V` are ineligible for this product offering.',
            'The insured must occupy the residence in order to be eligible for this product offering.',
        ];

        let statusSummary: NewBusinessApplicationStatusSummary = new NewBusinessApplicationStatusSummary();
        statusSummary.isApplicationDocumentAvailable = true;
        statusSummary.status = status;
        statusSummary.sections = sections;
        statusSummary.eligibility = eligibilityStatus;

        newBusinessApplication.status = statusSummary;

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(newBusinessApplication);
            }, 1000);
        });
    }

    public async deleteBuilding(buildingGuid: string): Promise<NewBusinessApplication> {
        throw new Error("Method not implemented.");
    }

    public async saveInsuredInfo(newBusinessApplicationId: number,
        insuredInfo: InsuredInfo): Promise<NewBusinessApplication> {

        throw new Error("Method not implemented.");
    }

    public async addMortgagee(newBusinessApplicationId: number,
        mortgagee: MortgageeRequest): Promise<MortgageeRequestWithStatusAndId> {

        throw new Error("Method not implemented.");
    }

    public async updateMortgagee(mortgageeGuid: string,
        mortgagee: MortgageeRequest): Promise<MortgageeRequestWithStatusAndId> {

        throw new Error("Method not implemented.");
    }

    public async deleteMortgagee(mortgageeGuid: string): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public async confirmNoMortgagees(newBusinessApplicationId: number): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public async validateMortgagees(newBusinessApplicationId: number): Promise<void> {
        throw new Error('Method not implemented.');
    }

    public async saveInceptionDate(newBusinessApplicationId: number,
        inceptionDate: Date): Promise<void> {

        throw new Error("Method not implemented.");
    }

    public async submitNewBusinessApplication(newBusinessApplicationId: number,
        acknowledgements: AcknowledgementText[],
        signature: Signature): Promise<NewBusinessApplication> {

        throw new Error("Method not implemented.");
    }

    public async unsubmitNewBusinessApplication(newBusinessApplicationId: number): Promise<NewBusinessApplication> {
        throw new Error("Method not implemented.");
    }

    public async resubmitNewBusinessApplication(newBusinessApplicationId: number, note: string, document: DocumentUpload | undefined): Promise<NewBusinessApplication> {
        throw new Error('Method not implemented.');
    }

    public async getPossibleDisclosureTexts(newBusinessApplicationId: number): Promise<PossibleDisclosureTexts> {
        throw new Error("Method not implemented.");
    }

    public async getBindingAuthorityEligibilityResults(newBusinessApplicationId: number): Promise<BindingAuthorityEligibilityResult[]> {
        throw new Error("Method not implemented.");
    }

    public async getNewBusinessApplicationDocument(newBusinessApplicationId: number): Promise<NewBusinessApplicationDocument> {
        throw new Error("Method not implemented.");
    }

    public async setTargetWorkflowVersion(newBusinessApplicationId: number, newVersionNumber: number): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public getPossiblePerilsAndDeductibles(newBusinessApplicationId: number): Promise<PossiblePeril[]> {
        throw new Error('Method not implemented.');
    }
};